import { ANNOTATION_FIELD_TYPE } from '@/constants/annotation';
import {
  AnnotationTemplate,
  AnnotationTemplateUrlField,
  AnnotationUrl,
  AnnotationUrlField,
} from '@/graphql/codegen/graphql';
import { editAnnotationUpdateField, useExplore } from '@/stores/explore';
import { cn } from '@/utils/classname';
import { Input, inverted, Tooltip, TooltipContent, TooltipTrigger } from '@skand/ui';
import { Fragment, useCallback, useState } from 'react';
import { PencilIcon } from '../IconButton/PencilIcon';

export interface AnnotationFieldsProps {
  template: AnnotationTemplate;
}

export const FieldLink = ({
  field,
  disabled = false,
}: {
  field: AnnotationTemplateUrlField;
  disabled: boolean;
}) => {
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const urls = useExplore(state => {
    const urlField = state.annotationDraft?.fields?.find(item => item && item.fieldId === field.id);
    return (urlField?.urls ?? []) as AnnotationUrl[];
  });

  const handleChangeName = useCallback(
    (index: number, name: string) => {
      editAnnotationUpdateField(field, ANNOTATION_FIELD_TYPE.URL, prev => {
        const oldUrls = (prev as AnnotationUrlField).urls ?? [];
        const newUrls = [];
        for (let i = 0; i < oldUrls.length; i++) {
          if (i !== index) newUrls.push(oldUrls[i]);
          else newUrls.push({ ...oldUrls[i], name });
        }

        return { ...prev, urls: newUrls };
      });
    },
    [field],
  );

  const handleChangeUrl = useCallback(
    (index: number, url: string) => {
      editAnnotationUpdateField(field, ANNOTATION_FIELD_TYPE.URL, prev => {
        const oldUrls = (prev as AnnotationUrlField).urls ?? [];
        const newUrls = [];
        for (let i = 0; i < oldUrls.length; i++) {
          if (i !== index) newUrls.push(oldUrls[i]);
          else newUrls.push({ ...oldUrls[i], url });
        }

        return { ...prev, urls: newUrls };
      });
    },
    [field],
  );

  const handleAddLink = useCallback(() => {
    if (disabled) return;

    setEnableEdit(true);
    editAnnotationUpdateField(field, ANNOTATION_FIELD_TYPE.URL, prev => {
      const oldUrls = (prev as AnnotationUrlField).urls ?? [];
      return { ...prev, urls: [...oldUrls, { name: undefined, url: undefined }] };
    });
  }, [field, disabled]);

  const handleRemove = useCallback(
    (index: number) => {
      editAnnotationUpdateField(field, ANNOTATION_FIELD_TYPE.URL, prev => {
        const oldUrls = (prev as AnnotationUrlField).urls ?? [];
        return { ...prev, urls: oldUrls.filter((_, i) => i !== index) };
      });
    },
    [field],
  );

  return (
    <div
      className={cn(
        'mt-3',
        'w-full',
        'flex',
        'flex-col',
        'gap-2',
        'border-solid',
        'border-neutral-400',
        'rounded-4px',
        'border-1px',
        'p-3',
      )}
      key={field.name}
    >
      <p className="color-neutral-800 typo-text-xxs">{field.name}</p>
      <div className="grid grid-cols-[repeat(2,minmax(0,1fr))_auto] w-full items-center gap-x-2 gap-y-1 overflow-x-hidden">
        {urls.map(({ name, url }, index) => {
          return (
            <Fragment key={index}>
              {enableEdit ? (
                <Input
                  onChange={value => handleChangeName(index, value)}
                  placeholder="Link"
                  value={name ?? ''}
                />
              ) : (
                <span
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-nowrap color-neutral-600 typo-text-s"
                  title={name ?? ''}
                >
                  {name ?? ''}
                </span>
              )}

              {enableEdit ? (
                <Input
                  onChange={value => handleChangeUrl(index, value)}
                  placeholder="URL"
                  tail={
                    <div className={cn('i-skand-link', 'color-neutral-400', 'text-4', 'ml-1')} />
                  }
                  value={url ?? ''}
                />
              ) : (
                <a
                  className={cn(
                    'overflow-hidden text-ellipsis whitespace-nowrap text-nowrap color-neutral-600 typo-link-xs',
                  )}
                  href={url ?? ''}
                  rel="noreferrer"
                  target="_blank"
                  title={url ?? ''}
                >
                  {url}
                </a>
              )}

              <div className="flex items-center justify-center gap-2">
                {!disabled && !enableEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          'color-neutral-500 cursor-pointer hover:color-primary-400  flex items-center justify-center',
                        )}
                        onClick={() => setEnableEdit(true)}
                      >
                        <PencilIcon />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent align="center" side="left" sideOffset={8}>
                      <div className={inverted}>Edit link</div>
                    </TooltipContent>
                  </Tooltip>
                )}

                {!disabled && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          'i-skand-close w-3 color-neutral-500 cursor-pointer hover:color-primary-400 flex items-center justify-center',
                        )}
                        onClick={() => {
                          handleRemove(index);
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent align="center" side="left" sideOffset={8}>
                      <div className={inverted}>Remove link</div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      <div className="ml-auto flex justify-end" onClick={handleAddLink}>
        <p
          className={cn(
            'typo-link-xs',
            disabled ? 'color-neutral-400' : 'cursor-pointer color-neutral-800 ',
          )}
        >
          + Add Link
        </p>
      </div>
    </div>
  );
};
